import React from 'react';
import PropTypes from 'prop-types';
import { IndexRoute, Router, Route, hashHistory } from 'react-router';
import { Provider } from 'react-redux';

import RouteTypes from '../constants/RouteTypes';

import App from '../components/app/App';
import AutoSubmit from '../components/autoSubmit/AutoSubmit';
import TableOfContents from '../components/tableOfContents/TableOfContents';
import DisplayTypeContainer from '../components/displayTypeWrapper/DisplayTypeContainer';
import SectionQuestionsDisplayContainer from '../components/sectionview/SectionQuestionsDisplay';

import OptionsAddExtraTime from '../components/options/OptionsAddExtraTime';
import OptionsContainer from '../components/options/OptionsContainer';
import OptionsContrast from '../components/options/OptionsContrast';
import OptionsTTS from '../components/options/OptionsTTS';
import OptionsDeliverBlank from '../components/options/OptionsDeliverBlank';
import OptionsHideTimeRemaining from '../components/options/OptionsHideTimeRemaining';
import OptionsExitSeb from '../components/options/OptionsExitSeb';
import OptionsExitChromeApp from '../components/options/OptionsExitChromeApp';
import OptionsFileDelivery from '../components/options/OptionsFileDelivery';
import OptionsFileDeliveryChromeApp from '../components/options/OptionsFileDeliveryChromeApp';
import OptionsFileDelivered from '../components/options/OptionsFileDelivered';
import OptionsList from '../components/options/OptionsList';
import OptionsLanguage from '../components/options/OptionsLanguage';
import OptionsResumePassword from '../components/options/OptionsResumePassword';
import OptionsWithdraw from '../components/options/OptionsWithdraw';
import OptionsZoomFactor from '../components/options/OptionsZoomFactor';
import OptionsTestInstructions from '../components/options/OptionsTestInstructions';
import InvigilatorOptionsList from '../components/options/InvigilatorOptionsList';

import AllViewContainer from '../components/allView/AllViewContainer';
import ResultPageContainer from '../components/result/ResultPage';
import TestOverviewContainer from '../components/testOverview/TestOverviewContainer';
import IdVerificationContainer from '../components/idverification/IdVerification';
import RoomScanContainer from '../components/roomscan/RoomScan';
import MessagesContainer from '../components/messages/MessagesPage';

import PermissionDeniedPage from '../components/error/PermissionDeniedPage';
import InvalidTabToken from '../components/error/InvalidTabTokenPage';
import EmptyQuestionSetPage from '../components/error/EmptyQuestionSetPage';
import RouteNotFound from '../components/RouteNotFound/RouteNotFound';

import { StimulusDisplayContainer } from '../components/stimulusDisplay/StimulusDisplay';
import LoginContainer from '../components/login/Login';

import navigationService from '../services/NavigationService';
import storeManager from '../store/StoreManager';
import viewReadyTracker from '../services/ViewReadyTracker';
import hypothesisService from '../services/HypothesisService';
import sessionReader from '../store/readHelpers/SessionReader';
import tocReader from '../store/readHelpers/TocReader';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import annotationService from '../services/AnnotationService';
import RemoteOralExam from '../components/remoteOralExam/RemoteOralExam';
import responseReader from '../store/readHelpers/ResponseReader';
import questionReader from '../store/readHelpers/QuestionReader';

export function onEnter(nextState, redirectTo) {
    const { location, params } = nextState;

    if (location.action === 'POP' && navigationService.isNavigationLocked()) {
        // Preventing back button
        const state = storeManager.getState();
        redirectTo(state.currentlyShown.route);
        navigationService.handleNavigationLockMessage();
    } else if (Routing.shouldRedirectToLogin(location.pathname)) {
        redirectTo('/login');
    } else if (Routing.shouldRedirectToInvigilatorOptions(location.pathname)) {
        redirectTo('/options/invigilator');
    } else if (Routing.shouldRedirectToTestOverview(location.pathname)) {
        redirectTo('/test-overview');
    } else if (Routing.shouldRedirectFromTestOverview(location.pathname)) {
        navigationService.navigateToStartingPoint();
    } else if (Routing.shouldRedirectToDelivering(location.pathname)) {
        navigationService.navigateToDelivering();
    } else if (Routing.shouldRedirectToFirstUncommittedQuestion(location.pathname, params.questionId)) {
        const firstUncommittedQuestion = responseReader.getFirstUncommittedQuestion();
        navigationService.navigateToQuestion(firstUncommittedQuestion.contentRevisionId, firstUncommittedQuestion.sectionId, params.skipScroll);
    } else {
        viewReadyTracker.setLocation(location.pathname, params);
        Routing.updateState(location, params);
        if (sessionReader.isAnnotationMode()) {
            hypothesisService.appendDCMIMetaDataTagsAndInitHypothesisIfNeeded();
        }
        annotationService.dispatchNavigationEvent();
    }
}

export function onQuestionEnter(nextState, redirectTo) {
    const { questionId, skipScroll } = nextState.params;
    const sectionId = tocReader.getSectionIdForQuestionId(questionId);
    const pathname = navigationService.getPathForQuestion(questionId, sectionId, skipScroll);
    const location = { ...nextState.location, pathname };
    const params = { questionId, sectionId, skipScroll };
    const newState = { ...nextState, location, params };
    return onEnter(newState, redirectTo);
}

const routes = (
    <Route path="/" component={App} onEnter={onEnter}>
        <IndexRoute component={TableOfContents} onEnter={onEnter} />
        <Route path="question/:questionId(/:skipScroll)" component={DisplayTypeContainer} onEnter={onQuestionEnter} />
        <Route path="remote-oral" component={RemoteOralExam} onEnter={onEnter} />
        <Route path="section/:sectionId/question/:questionId(/:skipScroll)" component={DisplayTypeContainer} onEnter={onEnter} />
        <Route
            path="section/:sectionId/question/:questionId/scorableItem/:scorableItemOrderNumber(/:skipScroll)"
            component={DisplayTypeContainer}
            onEnter={onEnter}
        />
        <Route path="section/:sectionId/stimulus" component={StimulusDisplayContainer} onEnter={onEnter} />
        <Route path="section/:sectionId" component={SectionQuestionsDisplayContainer} onEnter={onEnter} />
        <Route path="all" component={AllViewContainer} onEnter={onEnter} />
        <Route path="delivering" component={TableOfContents} onEnter={onEnter} />
        <Route path="result" component={ResultPageContainer} onEnter={onEnter} />
        <Route path="test-overview" component={TestOverviewContainer} onEnter={onEnter} />
        <Route path="room-scan" component={RoomScanContainer} onEnter={onEnter} />
        <Route path="id-verification" component={IdVerificationContainer} onEnter={onEnter} />
        <Route path="options" component={OptionsContainer} onEnter={onEnter}>
            <IndexRoute component={OptionsList} onEnter={onEnter} />
            <Route path="add-extra-time" component={OptionsAddExtraTime} onEnter={onEnter} />
            <Route path="back-to-dashboard" component={OptionsContainer} onEnter={onEnter} />
            <Route path="contrast" component={OptionsContrast} onEnter={onEnter} />
            <Route path="tts" component={OptionsTTS} onEnter={onEnter} />
            <Route path="distraction-free-mode" component={OptionsHideTimeRemaining} onEnter={onEnter} />
            <Route path="zoom-factor" component={OptionsZoomFactor} onEnter={onEnter} />
            <Route path="test-instructions" component={OptionsTestInstructions} onEnter={onEnter} />
            <Route path="invigilator" component={OptionsContainer} onEnter={onEnter}>
                <IndexRoute component={InvigilatorOptionsList} onEnter={onEnter} />
            </Route>
            <Route path="deliver-blank" component={OptionsDeliverBlank} onEnter={onEnter} />
            <Route path="exit-seb" component={OptionsExitSeb} onEnter={onEnter} />
            <Route path="exit-chrome-app" component={OptionsExitChromeApp} onEnter={onEnter} />
            <Route path="resume-password" component={OptionsResumePassword} onEnter={onEnter} />
            <Route path="language" component={OptionsLanguage} onEnter={onEnter} />
            <Route path="withdraw" component={OptionsWithdraw} onEnter={onEnter} />
            <Route path="file-delivery" component={OptionsFileDelivery} onEnter={onEnter} />
            <Route path="file-delivery-chrome-app" component={OptionsFileDeliveryChromeApp} onEnter={onEnter} />
            <Route path="file-delivered" component={OptionsFileDelivered} onEnter={onEnter} />
        </Route>
        <Route path="messages" component={MessagesContainer} onEnter={onEnter} />
        <Route path="login" component={LoginContainer} onEnter={onEnter} />
        <Route path="auto-submit" component={AutoSubmit} onEnter={onEnter} />

        <Route path="/error/403" component={PermissionDeniedPage} onEnter={onEnter} key="error-1" />
        <Route path="/error/invalid-tab-token" component={InvalidTabToken} onEnter={onEnter} key="error-2" />
        <Route path="/error/empty-question-set" component={EmptyQuestionSetPage} onEnter={onEnter} key="error-3" />

        <Route path="*" component={RouteNotFound} onEnter={onEnter} />
    </Route>
);

class Routing extends React.Component {
    static propTypes = {
        store: PropTypes.object.isRequired,
    };

    static updateState(location, params) {
        const state = storeManager.getState();
        if (state.currentlyShown.route !== location.pathname) {
            navigationService.handleLocationChange(location, params);
        }
    }

    static shouldRedirectToLogin(pathname) {
        return pathname !== '/login'
            && sessionReader.isLoginRequired()
            && !sessionReader.isLoggedIn();
    }

    static shouldRedirectToInvigilatorOptions(pathname) {
        return pathname.includes('invigilator/') && !sessionReader.isInvigilatorAuthorized();
    }

    static shouldRedirectToTestOverview(pathname) {
        return (
            sessionReader.supportsTestOverview() &&
            !assessmentReader.isAssessmentLoaded() &&
            !pathname.includes(RouteTypes.ROUTE_TYPE_OPTIONS) &&
            !pathname.includes(RouteTypes.ROUTE_TYPE_MESSAGES) &&
            !pathname.includes(RouteTypes.ROUTE_TYPE_TEST_OVERVIEW) &&
            !pathname.includes(RouteTypes.ROUTE_TYPE_ROOM_SCAN) &&
            !pathname.includes(RouteTypes.ROUTE_TYPE_ID_VERIFICATION)
        );
    }

    static shouldRedirectFromTestOverview(pathname) {
        return pathname.includes('test-overview') && !sessionReader.supportsTestOverview();
    }

    /**
     * If the exam is configured as one-way-navigation and supports committing answers
     * then player must check that the current question is the first uncommitted yet question.
     * Otherwise player must prevent rendering it and redirect the student to such question.
     */
    static shouldRedirectToFirstUncommittedQuestion(pathname, questionId) {
        const currentQuestion = assessmentReader.getQuestion(questionId);

        if (!questionId ||
            !pathname.includes(RouteTypes.ROUTE_TYPE_QUESTION) ||
            !sessionReader.isOneWayNavigationSupportedContext() ||
            !questionReader.shouldCommitAnswerTimestamp(currentQuestion)) {
            return false;
        }

        const firstUncommittedQuestion = responseReader.getFirstUncommittedQuestion();
        return !!firstUncommittedQuestion && firstUncommittedQuestion.contentRevisionId !== parseInt(questionId, 10);
    }

    /**
     * Redirects to the delivering page if current test supports committing answers and all the answers are already committed
     */
    static shouldRedirectToDelivering(pathname) {
        return pathname.includes(RouteTypes.ROUTE_TYPE_QUESTION) &&
            sessionReader.isOneWayNavigationSupportedContext() &&
            sessionReader.isCommittingAnswerSupported(true) &&
            responseReader.areAllQuestionsCommitted();
    }

    render() {
        return (
            <Provider store={this.props.store}>
                <Router history={hashHistory}>
                    {routes}
                </Router>
            </Provider>
        );
    }
}

export default Routing;
